import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import MerchantForm from '../../../Component/Merchant/BasicInfo';
import MerchantInformation from '../../../Component/Merchant/MerchantInformation';
import Document from '../../../Component/Merchant/Document';
import BankAccountManagement from '../../../Component/Merchant/BankAccountManagement';
import axiosInstance from '../../../../axiosConfig';

const NavigationTabs = () => {
    const [searchParams] = useSearchParams();
    const uuidFromUrl = searchParams.get('uuid');
    
    const [entityUuid, setEntityUuid] = useState(uuidFromUrl || '');
    const [entityId, setEntityId] = useState(null); // Add entityId state
    const [entityName, setEntityName] = useState(''); // Add entity name state
    const [entityType, setEntityType] = useState(''); // Add entity type state
    const [entityCode, setEntityCode] = useState(''); // Add entity code (registration) state
    const [entityWebsite, setEntityWebsite] = useState(''); // Add website state
    const [entityDescription, setEntityDescription] = useState(''); // Add description state
    const [entityTags, setEntityTags] = useState(''); // Add tags state
    const [activeTab, setActiveTab] = useState('basic');
    const [isLoadingEntity, setIsLoadingEntity] = useState(false);

    // Fetch entity data when UUID is provided in URL
    useEffect(() => {
        const fetchEntityData = async () => {
            if (!uuidFromUrl) return;
            
            setIsLoadingEntity(true);
            try {
                const response = await axiosInstance.get(
                    `/internal-api/v1/client-web/entity-panel/config/list?uuid=${uuidFromUrl}`
                );
                
                if (response.data && response.data.data && response.data.data.length > 0) {
                    const entity = response.data.data[0];
                    setEntityUuid(entity.uuid);
                    setEntityId(entity.id);
                    setEntityName(entity.name);
                    setEntityType(entity.entityType);
                    setEntityCode(entity.registration || entity.entityCode || '');
                    setEntityWebsite(entity.website || '');
                    setEntityDescription(entity.description || '');
                    setEntityTags(entity.tags || '');
                }
            } catch (error) {
                console.error('Error fetching entity data:', error);
                alert('Failed to load merchant data. Please try again.');
            } finally {
                setIsLoadingEntity(false);
            }
        };

        fetchEntityData();
    }, [uuidFromUrl]);

    const tabs = [
        { id: 'basic', label: 'Basic Info' },
        { id: 'location', label: 'Branch' },
        { id: 'images', label: 'Document' },
        { id: 'contact', label: 'Bank Details' },
    ];

    return (
        <div className="bg-white border rounded-xl border-slate-200">
            <div className="px-6 pt-6">
                <h1 className="text-xl font-bold text-slate-900">
                    {uuidFromUrl ? 'Edit Merchant Information' : 'Merchant Information'}
                </h1>

                {/* Tabs */}
                <div className="flex mt-4 border-b border-slate-200">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`px-4 py-3 mr-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                activeTab === tab.id
                                    ? 'text-brand-blue border-brand-blue'
                                    : 'text-slate-500 border-transparent hover:text-slate-700'
                            }`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoadingEntity && (
                <div className="flex items-center justify-center gap-2 p-10 text-sm text-slate-500">
                    <div className="w-4 h-4 border-b-2 rounded-full animate-spin border-brand-blue" />
                    Loading merchant data...
                </div>
            )}

            {/* Tab Contents */}
            {!isLoadingEntity && activeTab === 'basic' && (
                <div className="p-6">
                    <MerchantForm
                        isEditMode={!!uuidFromUrl}
                        entityUuid={entityUuid}
                        setEntityUuid={setEntityUuid}
                        entityId={entityId} // Pass entityId
                        setEntityId={setEntityId} // Pass setEntityId to MerchantForm
                        entityName={entityName} // Pass entity name to populate form
                        setEntityName={setEntityName} // Pass setEntityName
                        entityType={entityType} // Pass entity type to populate form
                        setEntityType={setEntityType} // Pass setEntityType
                        entityCode={entityCode} // Pass entity code to populate form
                        setEntityCode={setEntityCode} // Pass setEntityCode
                        entityWebsite={entityWebsite} // Pass website
                        setEntityWebsite={setEntityWebsite}
                        entityDescription={entityDescription} // Pass description
                        setEntityDescription={setEntityDescription}
                        entityTags={entityTags} // Pass tags
                        setEntityTags={setEntityTags}
                        setActiveTab={setActiveTab}
                    />
                </div>
            )}

            {!isLoadingEntity && activeTab === 'location' && (
                <div className="p-6">
                    <MerchantInformation
                        entityUuid={entityUuid}
                        entityId={entityId} // Pass entityId as prop
                        entityName={entityName} // Pass entity name
                        entityType={entityType} // Pass entity type
                        entityCode={entityCode} // Pass entity code
                        setActiveTab={setActiveTab}
                    />
                </div>
            )}

            {!isLoadingEntity && activeTab === 'images' && (
                <div className="p-6">
                    <Document
                        entityUuid={entityUuid}
                        entityId={entityId} // Pass entity ID
                        entityName={entityName} // Pass entity name
                        entityType={entityType} // Pass entity type
                        entityCode={entityCode} // Pass entity code
                        setActiveTab={setActiveTab}
                    />
                </div>
            )}

            {!isLoadingEntity && activeTab === 'contact' && (
                <div className="p-6">
                    <BankAccountManagement
                        entityUuid={entityUuid}
                        entityId={entityId} // Pass entity ID
                        entityName={entityName} // Pass entity name
                        entityType={entityType} // Pass entity type
                        entityCode={entityCode} // Pass entity code
                        setActiveTab={setActiveTab}
                    />
                </div>
            )}
        </div>
    );
};

export default NavigationTabs;